// T55 — PostHog Cloud EU client-side analytics wrapper.
// Consent-gated: PostHog initialises only after analytics consent is granted (T35).
// This module must never be imported from server-only files (*.server.ts).

import type { PostHog, PostHogConfig } from "posthog-js";
import type { AnalyticsEnvironment } from "~/lib/analytics/events";
import { restoreHeldPageContext } from "~/lib/analytics/held-page-context";
import { keepToIdentity, startIdentityScope } from "~/lib/analytics/identity-scope";
import { sanitizeCapturedEvent } from "~/lib/analytics/sanitize-url";
import { type ConsentState, hasConsent } from "~/lib/consent";

const POSTHOG_EU_HOST = "https://eu.i.posthog.com";

// Module singleton. Correct in production (one fresh module per page load). In dev
// with HMR the prior instance's flag is lost on hot-reload, so initAnalytics may
// re-run after a refresh — hard-reload to reset if dev-mode analytics look off.
let initialised = false;
let posthog: PostHog | undefined;
let loading: Promise<void> | undefined;
/** `posthog.init` has run in this page; separate from `initialised`, which tracks capture being on. */
let sdkInitialised = false;
/**
 * T975 — every request sent while this consent lasts carries its signal; a withdrawal aborts it.
 * The SDK's retry queue resends a failed request on a timer without looking at consent; a
 * stored retry keeps the signal of its first attempt, so it is aborted too, even after a
 * re-grant opens a new one.
 */
let consentSignal: AbortController | undefined;

/** Download only; the Provider rechecks live consent before initializing the SDK. */
export function loadAnalytics(): Promise<void> | undefined {
  if (typeof window === "undefined" || posthog) return;
  loading ??= import("posthog-js").then((sdk) => { posthog = sdk.default; }).catch((error: unknown) => {
    loading = undefined;
    throw error;
  });
  return loading;
}

/**
 * Initialise PostHog. Self-protecting: no-op unless analytics consent is present,
 * mirroring the guard in captureEvent so the function is safe to call defensively.
 * Idempotent — safe to call multiple times after the first successful init.
 *
 * T817 — identity is no longer an init argument. It was only honoured on the first call
 * (the `initialised` guard returned before it), so a login after init never identified
 * anyone; `syncIdentity` owns it now and runs on every change.
 */
export function initAnalytics(
  apiKey: string,
  consent: ConsentState | null,
  /**
   * The signed-in member (or null), when the caller knows it. Used only to drop a stale
   * identity BEFORE capturing is switched on: `opt_in_capturing()` sends `$opt_in`, and on
   * a full reload after logout or an account switch that event used to go out under the
   * previous member's id (independent review, PR #1836, m3). Identifying the new member
   * still happens in `syncIdentity`, after opt-in, so the `$identify` event is sent.
   */
  userId?: string | null,
  /**
   * T817 — the deployment label. Stamped on EVERY event in `before_send`, including the
   * ones the SDK sends by itself (`$opt_in`, `$identify`), which never pass through the
   * app's `capture` wrapper and so arrived unlabelled in production. An `environment` the
   * caller already set is kept.
   */
  environment?: AnalyticsEnvironment,
  /**
   * T975 — the analytics grant happened in this page (banner or preferences, a re-prompt
   * included), so it is a new decision even when the SDK still remembers an older opt-in.
   */
  options?: { grantedInThisPage?: boolean },
): void {
  if (typeof window === "undefined") return;
  if (!hasConsent("analytics", consent)) return;
  if (initialised || !posthog) return;

  // `init` once per page: a withdrawal only switches capture off (`optOutAnalytics`), so a
  // re-grant switches it back on instead of re-initialising (T975).
  if (!sdkInitialised) posthog.init(apiKey, {
    api_host: POSTHOG_EU_HOST,
    opt_out_capturing_by_default: true,
    capture_pageview: false,
    autocapture: false,
    persistence: "localStorage+cookie",
    // T975 — each event leaves when it is captured, while consent holds. The SDK's batch queue
    // flushes on a timer that does not look at consent, so a batched event captured just
    // before a withdrawal was sent after it. One request per event; billing counts events.
    request_batching: false,
    // T78 — PostHog's remote-config host (`eu-assets.i.posthog.com`) is not in our CSP and
    // we use capture only. Disabled rather than widening `script-src` to a third-party
    // host for features already turned off above; see T78's spec for the full trade.
    disable_external_dependency_loading: true,
    advanced_disable_flags: true,
    // T817 — every event, including the SDK's own `$pageview` / `$opt_in`, leaves with its
    // URLs reduced to origin + path (+ `utm_*`): reset/verify/unsubscribe links carry
    // tokens in the query. See `sanitize-url.ts`.
    before_send: (event) => {
      if (environment && event?.properties && event.properties.environment === undefined) {
        event.properties.environment = environment;
      }
      return keepToIdentity(sanitizeCapturedEvent(restoreHeldPageContext(event)));
    },
  });

  if (userId !== undefined && posthog.get_property("$user_state") === "identified") {
    if (posthog.get_distinct_id() !== userId) startNewIdentity(posthog);
  }

  // A re-grant in the same page turns persistence back on (switched off at the withdrawal) and
  // starts a new identity with a new device id too (plain `reset` keeps `$device_id`): the
  // refusal cleared the old ones from storage, and nothing from before it may be linked to
  // what comes after. A member is identified again by `syncIdentity`.
  if (sdkInitialised) {
    posthog.set_config({ disable_persistence: false });
    if (options?.grantedInThisPage) startNewIdentity(posthog, { newDevice: true });
  }
  sdkInitialised = true;
  consentSignal = new AbortController();
  // `fetch_options` is spread into every `fetch` the SDK makes, after its own options: this
  // signal replaces the SDK's 60 s per-request timeout. Typed for `cache` only, hence the cast;
  // `975-withdrawal-retries-nothing-after.test.tsx` pins that the SDK still honours it.
  posthog.set_config({ fetch_options: { signal: consentSignal.signal } as PostHogConfig["fetch_options"] });
  // `$opt_in` marks a decision, not a page load (T975): sent when the SDK is not opted in,
  // or when the grant was made in this page. A reload, or a preferences save that leaves
  // analytics on, is not a decision and sends none. Nothing is stored for this.
  if (!posthog.has_opted_in_capturing() || options?.grantedInThisPage) posthog.opt_in_capturing();
  initialised = true;
}

/** Properties allowed in event payloads. PII (email, phone, name, password) is forbidden. */
type EventProperties = Record<string, string | number | boolean | null | undefined>;

/**
 * Capture a client-side analytics event.
 * No-op when analytics consent has not been granted or PostHog is not initialised.
 */
export function captureEvent(
  event: string,
  properties: EventProperties,
  consent: ConsentState | null,
  /** T975 — an event held while the SDK downloaded keeps the moment it happened. */
  options?: { timestamp?: Date },
): void {
  if (typeof window === "undefined") return;
  if (!hasConsent("analytics", consent)) return;
  if (!initialised || !posthog) return;

  if (options?.timestamp) posthog.capture(event, properties, { timestamp: options.timestamp });
  else posthog.capture(event, properties);
}

/**
 * T78 — PostHog's own anonymous id for this browser, or `null` when analytics are not
 * running (no consent, not initialised, SSR).
 *
 * Exists so the client can hand its id to the worker on the `/api/leads` POST: the
 * auto-test funnel is half client-side (`started`, `result_viewed`, `waitlist_cta_clicked`)
 * and half server-side (`completed`), and without a shared `distinct_id` PostHog sees two
 * unrelated people and the funnel never joins up. It is a random uuid, never the email.
 */
export function getDistinctId(): string | null {
  if (typeof window === "undefined") return null;
  if (!initialised || !posthog) return null;
  return posthog.get_distinct_id() ?? null;
}

/**
 * T817 — align PostHog's identity with the signed-in member.
 *
 * - Signed in as `userId`, browser anonymous → `identify(userId)`: the anonymous visit
 *   that led to the login joins her (one journey, one person).
 * - Signed in as `userId`, browser identified as someone else → a new identity first, then
 *   identify: two accounts on one device must never merge into one person.
 * - Signed out, browser identified → a new identity: what happens next is a new anonymous id.
 *
 * The id is the internal member UUID — pseudonymous, never the email. Reads the SDK's own
 * persisted state rather than a module flag, so it is right after a reload too. No-op
 * until initialised (no consent, no identity).
 */
export function syncIdentity(userId: string | null): void {
  if (typeof window === "undefined") return;
  if (!initialised || !posthog) return;
  const identified = posthog.get_property("$user_state") === "identified";
  const current = posthog.get_distinct_id();
  if (userId) {
    if (current === userId) return;
    if (identified) startNewIdentity(posthog);
    posthog.identify(userId);
    return;
  }
  if (identified) startNewIdentity(posthog);
}

/**
 * T975 — the one way to start a new identity: a new anonymous id (and device id when asked),
 * with nothing that points back at the one before. posthog-js's `reset()` also drops the
 * opt-in, so capture would stop for the rest of the page and the next load would send an
 * `$opt_in` no decision made: it is restored silently, since consent did not change. And the
 * SDK's remembered pageview is scoped out (`identity-scope.ts`).
 */
function startNewIdentity(sdk: PostHog, options?: { newDevice?: boolean }): void {
  const optedIn = sdk.has_opted_in_capturing();
  sdk.reset(options?.newDevice);
  startIdentityScope();
  if (optedIn) sdk.opt_in_capturing({ captureEventName: false });
}

/**
 * Called on consent withdrawal. Stops capturing and signals PostHog to purge.
 * Callers should also submit a GDPR erasure request via PostHog's API when email is available.
 */
export function optOutAnalytics(): void {
  if (typeof window === "undefined") return;
  if (!initialised || !posthog) return;
  consentSignal?.abort();
  posthog.opt_out_capturing();
  // T975 — and stop it persisting, so the identifier the Provider clears after a refusal is not
  // written back (a capture, an unload, a session tick would otherwise save it again).
  posthog.set_config({ disable_persistence: true });
  // Reset so a later consent re-grant can re-init; otherwise initAnalytics would
  // short-circuit on the `initialised` guard and PostHog would stay opted out.
  initialised = false;
}
